import React, { useCallback, useEffect, useState } from "react";
import { AdminModal, EmptyState, SkeletonLoader } from "../../components/admin";
import { ConfirmModal } from "../../components/common";
import AdminLayout from "../../components/layout/AdminLayout";
import { useToast } from "../../contexts/ToastContext";
import adminService, {
  AdminCourse,
  AdminUniversity,
  CreateCourseRequest,
  UpdateCourseRequest,
} from "../../services/api/adminService";

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow";

const levels = [
  { value: "BACHELOR", label: "Bachelor's" },
  { value: "MASTER", label: "Master's" },
  { value: "DOCTORATE", label: "PhD" },
  { value: "KOREAN_LANGUAGE_PROGRAM", label: "Korean Language Program" },
  { value: "DIPLOMA", label: "Diploma" },
  { value: "CERTIFICATE", label: "Certificate" },
];

const BASE_DOCS = [
  "FAMILY_REGISTRATION_CERTIFICATE",
  "PARENTS_CNIC_ENGLISH",
  "APPLICANT_CNIC",
  "PASSPORT_PHOTO",
  "MATRIC_MOFA_ATTESTED",
  "INTER_MOFA_ATTESTED",
  "MATRIC_MARKSHEET_MOFA",
  "INTER_MARKSHEET_MOFA",
];

function getDefaultDocReqs(level: string): string[] {
  switch (level) {
    case "KOREAN_LANGUAGE_PROGRAM":
    case "DIPLOMA":
    case "CERTIFICATE":
      return [...BASE_DOCS];
    case "BACHELOR":
      return [...BASE_DOCS, "IELTS_CERTIFICATE"];
    case "MASTER":
      return [...BASE_DOCS, "BACHELORS_TRANSCRIPT_HEC_MOFA", "IELTS_CERTIFICATE"];
    case "DOCTORATE":
      return [...BASE_DOCS, "BACHELORS_TRANSCRIPT_HEC_MOFA", "MASTERS_TRANSCRIPT_HEC_MOFA", "IELTS_CERTIFICATE"];
    default:
      return [...BASE_DOCS];
  }
}

const documentTypeGroups = [
  {
    label: "Language Proficiency",
    types: [
      { value: "IELTS_CERTIFICATE", label: "IELTS Certificate" },
      { value: "TOEFL_CERTIFICATE", label: "TOEFL Certificate" },
      { value: "LANGUAGE_CERTIFICATE", label: "Language Certificate" },
    ],
  },
  {
    label: "Academic Documents",
    types: [
      { value: "MATRIC_MOFA_ATTESTED", label: "Matric Certificate (MOFA Attested)" },
      { value: "INTER_MOFA_ATTESTED", label: "Intermediate Certificate (MOFA Attested)" },
      { value: "MATRIC_MARKSHEET_MOFA", label: "Matric Marksheet (MOFA Apostilled)" },
      { value: "INTER_MARKSHEET_MOFA", label: "Intermediate Marksheet (MOFA Apostilled)" },
      { value: "BACHELORS_TRANSCRIPT_HEC_MOFA", label: "Bachelor's Transcript (HEC/MOFA)" },
      { value: "MASTERS_TRANSCRIPT_HEC_MOFA", label: "Master's Transcript (HEC/MOFA)" },
      { value: "TRANSCRIPT", label: "Transcript" },
      { value: "DIPLOMA", label: "Diploma" },
    ],
  },
  {
    label: "Identity Documents",
    types: [
      { value: "PASSPORT", label: "Passport" },
      { value: "APPLICANT_CNIC", label: "Applicant CNIC" },
      { value: "PARENTS_CNIC_ENGLISH", label: "Parents' CNIC (English translation)" },
      { value: "FAMILY_REGISTRATION_CERTIFICATE", label: "Family Registration Certificate" },
      { value: "PASSPORT_PHOTO", label: "Passport Photo" },
    ],
  },
  {
    label: "Supporting Documents",
    types: [
      { value: "MOTIVATION_LETTER", label: "Motivation Letter" },
      { value: "RECOMMENDATION_LETTER", label: "Recommendation Letter" },
      { value: "CV", label: "CV / Resume" },
      { value: "PORTFOLIO", label: "Portfolio" },
      { value: "OTHER", label: "Other" },
    ],
  },
];

interface CourseFormProps {
  onSubmit: (e: React.FormEvent) => void;
  formData: CreateCourseRequest;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  handleDocReqToggle: (value: string) => void;
  universities: AdminUniversity[];
  isSubmitting: boolean;
  onClose: () => void;
}

const CourseForm: React.FC<CourseFormProps> = ({
  onSubmit,
  formData,
  handleInputChange,
  handleDocReqToggle,
  universities,
  isSubmitting,
  onClose,
}) => (
  <form onSubmit={onSubmit} className="space-y-5">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Course Title <span className="text-red-400">*</span>
        </label>
        <input type="text" name="title" value={formData.title} onChange={handleInputChange} required className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          University <span className="text-red-400">*</span>
        </label>
        <select name="universityId" value={formData.universityId} onChange={handleInputChange} required className={inputClass}>
          <option value="">Select University</option>
          {universities.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Level <span className="text-red-400">*</span>
        </label>
        <select name="level" value={formData.level} onChange={handleInputChange} required className={inputClass}>
          {levels.map((l) => (<option key={l.value} value={l.value}>{l.label}</option>))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Degree <span className="text-red-400">*</span>
        </label>
        <input type="text" name="degree" value={formData.degree} onChange={handleInputChange} required placeholder="e.g., Bachelor of Science" className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Duration (years) <span className="text-red-400">*</span>
        </label>
        <input type="number" name="duration" value={formData.duration} onChange={handleInputChange} required min="1" step="0.5" className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Study Mode <span className="text-red-400">*</span>
        </label>
        <select name="studyMode" value={formData.studyMode} onChange={handleInputChange} required className={inputClass}>
          <option value="FULL_TIME">Full Time</option>
          <option value="PART_TIME">Part Time</option>
          <option value="SANDWICH">Sandwich</option>
          <option value="DISTANCE_LEARNING">Distance Learning</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Language <span className="text-red-400">*</span>
        </label>
        <input type="text" name="language" value={formData.language} onChange={handleInputChange} required placeholder="e.g., English" className={inputClass} />
      </div>
    </div>

    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Course Details</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Short Name</label>
          <input type="text" name="shortName" value={formData.shortName || ""} onChange={handleInputChange} placeholder="e.g., CS" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Course Code</label>
          <input type="text" name="courseCode" value={formData.courseCode || ""} onChange={handleInputChange} placeholder="e.g., BSC-CS-2024" className={inputClass} />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Next Available Intake</label>
          <input type="text" name="nextAvailableIntake" value={formData.nextAvailableIntake || ""} onChange={handleInputChange} placeholder="e.g., September 2025" className={inputClass} />
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
          <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className={`${inputClass} resize-none`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Overview</label>
          <textarea name="overview" value={formData.overview || ""} onChange={handleInputChange} rows={4} placeholder="Brief overview of the course..." className={`${inputClass} resize-none`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Program Structure</label>
          <textarea name="programStructure" value={formData.programStructure || ""} onChange={handleInputChange} rows={4} placeholder="Describe the structure of the program, semesters, modules, etc." className={`${inputClass} resize-none`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Career Opportunities</label>
          <textarea name="careerOpportunities" value={formData.careerOpportunities || ""} onChange={handleInputChange} rows={4} placeholder="Describe career paths and opportunities for graduates..." className={`${inputClass} resize-none`} />
        </div>
      </div>
    </div>

    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Fees &amp; Costs</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Tuition Fee (USD) per semester</label>
          <input type="text" name="tuitionFeeUSD" value={formData.tuitionFeeUSD || ""} onChange={handleInputChange} placeholder="e.g., $5,000 / semester" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Tuition Fee (KRW) per semester</label>
          <input type="text" name="tuitionFeeKRW" value={formData.tuitionFeeKRW || ""} onChange={handleInputChange} placeholder="e.g., ₩6,500,000 / semester" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Online Application Fee</label>
          <input type="text" name="onlineApplicationFee" value={formData.onlineApplicationFee || ""} onChange={handleInputChange} placeholder="e.g., $50" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Matriculation / Enrollment Fee</label>
          <input type="text" name="matriculationFee" value={formData.matriculationFee || ""} onChange={handleInputChange} placeholder="e.g., $200" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Dormitory Fee</label>
          <input type="text" name="dormitoryFee" value={formData.dormitoryFee || ""} onChange={handleInputChange} placeholder="e.g., $300 / month" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Institution Handling Fee</label>
          <input type="text" name="institutionHandlingFee" value={formData.institutionHandlingFee || ""} onChange={handleInputChange} placeholder="e.g., $100" className={inputClass} />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Bank Statement Requirement</label>
          <input type="text" name="bankStatementRequirement" value={formData.bankStatementRequirement || ""} onChange={handleInputChange} placeholder="e.g., Minimum $10,000 balance required" className={inputClass} />
        </div>
      </div>
    </div>

    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Eligibility &amp; Requirements</p>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Eligibility</label>
          <textarea name="eligibility" value={formData.eligibility || ""} onChange={handleInputChange} rows={3} placeholder="Who is eligible to apply? (age, academic background, nationality, etc.)" className={`${inputClass} resize-none`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Requirements (General)</label>
          <textarea name="requirements" value={formData.requirements || ""} onChange={handleInputChange} rows={3} placeholder="General document or application requirements..." className={`${inputClass} resize-none`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Entry Requirements</label>
          <textarea name="entryRequirements" value={formData.entryRequirements || ""} onChange={handleInputChange} rows={3} placeholder="Minimum academic qualifications, GPA, etc." className={`${inputClass} resize-none`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Language Requirements</label>
          <textarea name="languageRequirements" value={formData.languageRequirements || ""} onChange={handleInputChange} rows={3} placeholder="IELTS / TOEFL minimum scores, language proficiency requirements..." className={`${inputClass} resize-none`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Other Requirements</label>
          <textarea name="otherRequirements" value={formData.otherRequirements || ""} onChange={handleInputChange} rows={3} placeholder="Any other requirements (interviews, portfolios, etc.)" className={`${inputClass} resize-none`} />
        </div>
      </div>
    </div>

    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Scholarships &amp; Financial Aid</p>
      <p className="text-xs text-gray-400 mb-3">Enter one entry per line. Format: <span className="font-mono bg-gray-100 px-1 rounded">IELTS 5.5 / iBT TOEFL 80 = 20%</span></p>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">First Semester Scholarships</label>
          <textarea name="firstSemesterScholarship" value={formData.firstSemesterScholarship || ""} onChange={handleInputChange} rows={4} placeholder={"IELTS 5.5 / iBT TOEFL 80 = 20%\nIELTS 6.0 / iBT TOEFL 85 = 30%\nIELTS 6.5 / iBT TOEFL 90 = 50%"} className={`${inputClass} resize-none font-mono text-xs`} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Scholarships from 2nd Semester Onwards</label>
          <textarea name="scholarshipFrom2ndSemester" value={formData.scholarshipFrom2ndSemester || ""} onChange={handleInputChange} rows={4} placeholder={"IELTS 5.5 / iBT TOEFL 80 = 20%\nIELTS 6.0 / iBT TOEFL 85 = 30%\nIELTS 6.5 / iBT TOEFL 90 = 50%"} className={`${inputClass} resize-none font-mono text-xs`} />
        </div>
      </div>
    </div>

    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Required Documents</p>
      <div className="space-y-4">
        {documentTypeGroups.map((group) => (
          <div key={group.label}>
            <p className="text-xs font-medium text-gray-500 mb-2">{group.label}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {group.types.map((doc) => {
                const checked = (formData.documentRequirements || []).includes(doc.value);
                return (
                  <label key={doc.value} className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleDocReqToggle(doc.value)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{doc.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="flex items-center gap-2">
      <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded" />
      <label className="text-sm text-gray-700">Active</label>
    </div>

    <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
      <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
        Cancel
      </button>
      <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50">
        {isSubmitting ? "Saving..." : "Save"}
      </button>
    </div>
  </form>
);

const CourseManagement: React.FC = () => {
  const [courses, setCourses] = useState<AdminCourse[]>([]);
  const [universities, setUniversities] = useState<AdminUniversity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<AdminCourse | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [universityFilter, setUniversityFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCourse, setDeletingCourse] = useState<AdminCourse | null>(null);

  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState<CreateCourseRequest>({
    title: "", shortName: "", description: "", overview: "",
    programStructure: "", careerOpportunities: "",
    universityId: "", level: "BACHELOR",
    duration: 0, studyMode: "FULL_TIME", degree: "", language: "",
    courseCode: "", nextAvailableIntake: "",
    eligibility: "", requirements: "",
    entryRequirements: "", languageRequirements: "", otherRequirements: "",
    tuitionFeeUSD: "", tuitionFeeKRW: "", onlineApplicationFee: "",
    matriculationFee: "", dormitoryFee: "", institutionHandlingFee: "",
    bankStatementRequirement: "",
    firstSemesterScholarship: "", scholarshipFrom2ndSemester: "",
    isActive: true, documentRequirements: [],
  });

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getCourses({
        page: currentPage, limit: 20,
        search: searchQuery || undefined,
        level: levelFilter || undefined,
        universityId: universityFilter || undefined,
      });
      if (response.data && Array.isArray(response.data)) {
        setCourses(response.data);
        setTotalPages((response as any).pagination?.totalPages || 1);
      } else {
        setCourses([]);
      }
    } catch (err: any) {
      showError("Failed to fetch courses");
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, levelFilter, universityFilter, showError]);

  const fetchUniversities = useCallback(async () => {
    try {
      const response = await adminService.getUniversities({ page: 1, limit: 100 });
      if (response.data && "universities" in response.data && Array.isArray(response.data.universities)) {
        setUniversities(response.data.universities);
      } else {
        setUniversities([]);
      }
    } catch (err: any) {
      setUniversities([]);
    }
  }, []);

  useEffect(() => { fetchCourses(); fetchUniversities(); }, [fetchCourses, fetchUniversities]);

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await adminService.createCourse(formData);
      showSuccess("Course created successfully");
      setShowCreateModal(false);
      resetForm();
      fetchCourses();
    } catch (err: any) {
      showError(err.message || "Failed to create course");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCourse) return;
    try {
      setIsSubmitting(true);
      const updateData: UpdateCourseRequest = { ...formData };
      await adminService.updateCourse(editingCourse.id, updateData);
      showSuccess("Course updated successfully");
      setShowEditModal(false);
      setEditingCourse(null);
      resetForm();
      fetchCourses();
    } catch (err: any) {
      showError(err.message || "Failed to update course");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCourse = (course: AdminCourse) => {
    setDeletingCourse(course);
    setShowDeleteModal(true);
  };

  const confirmDeleteCourse = async () => {
    if (!deletingCourse) return;
    setShowDeleteModal(false);
    try {
      await adminService.deleteCourse(deletingCourse.id);
      showSuccess("Course deleted successfully");
      fetchCourses();
    } catch (err: any) {
      showError(err.message || "Failed to delete course");
    } finally {
      setDeletingCourse(null);
    }
  };

  const openEditModal = (course: AdminCourse) => {
    setEditingCourse(course);
    setFormData({
      title: course.title,
      shortName: course.shortName || "",
      description: course.description || "",
      overview: course.overview || "",
      programStructure: course.programStructure || "",
      careerOpportunities: course.careerOpportunities || "",
      universityId: course.universityId,
      level: course.level as any,
      duration: course.duration,
      studyMode: course.studyMode as any,
      degree: course.degree,
      language: course.language || "",
      courseCode: course.courseCode || "",
      nextAvailableIntake: course.nextAvailableIntake || "",
      eligibility: course.eligibility || "",
      requirements: course.requirements || "",
      entryRequirements: course.entryRequirements || "",
      languageRequirements: course.languageRequirements || "",
      otherRequirements: course.otherRequirements || "",
      tuitionFeeUSD: course.tuitionFeeUSD || "",
      tuitionFeeKRW: course.tuitionFeeKRW || "",
      onlineApplicationFee: course.onlineApplicationFee || "",
      matriculationFee: course.matriculationFee || "",
      dormitoryFee: course.dormitoryFee || "",
      institutionHandlingFee: course.institutionHandlingFee || "",
      bankStatementRequirement: course.bankStatementRequirement || "",
      firstSemesterScholarship: course.firstSemesterScholarship || "",
      scholarshipFrom2ndSemester: course.scholarshipFrom2ndSemester || "",
      documentRequirements: course.documentRequirements && course.documentRequirements.length > 0
        ? course.documentRequirements
        : getDefaultDocReqs(course.level),
      isActive: course.isActive,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: "", shortName: "", description: "", overview: "",
      programStructure: "", careerOpportunities: "",
      universityId: "", level: "BACHELOR",
      duration: 0, studyMode: "FULL_TIME", degree: "", language: "",
      courseCode: "", nextAvailableIntake: "",
      eligibility: "", requirements: "",
      entryRequirements: "", languageRequirements: "", otherRequirements: "",
      tuitionFeeUSD: "", tuitionFeeKRW: "", onlineApplicationFee: "",
      matriculationFee: "", dormitoryFee: "", institutionHandlingFee: "",
      bankStatementRequirement: "",
      firstSemesterScholarship: "", scholarshipFrom2ndSemester: "",
      isActive: true, documentRequirements: getDefaultDocReqs("BACHELOR"),
    });
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      if (type === "checkbox") {
        setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
      } else if (type === "number") {
        setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
      } else if (name === "level") {
        setFormData((prev) => ({ ...prev, level: value as any, documentRequirements: getDefaultDocReqs(value) }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }, []
  );

  const handleDocReqToggle = useCallback((value: string) => {
    setFormData((prev) => {
      const current = prev.documentRequirements || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, documentRequirements: updated };
    });
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
            <p className="text-sm text-gray-500 mt-1">Manage courses in the platform</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors"
          >
            Add Course
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Search</label>
              <input type="text" placeholder="Search by title..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Level</label>
              <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} className={inputClass}>
                <option value="">All Levels</option>
                {levels.map((l) => (<option key={l.value} value={l.value}>{l.label}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">University</label>
              <select value={universityFilter} onChange={(e) => setUniversityFilter(e.target.value)} className={inputClass}>
                <option value="">All Universities</option>
                {universities.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Course</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">University</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Level</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Duration</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Fees</th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && courses.length === 0 ? (
                  <SkeletonLoader variant="table-row" count={5} columns={6} />
                ) : courses.length > 0 ? (
                  courses.map((course) => (
                    <tr key={course.id} className="border-b border-gray-50 hover:bg-cyan-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">{course.title}</p>
                        <p className="text-xs text-gray-400">{course.language}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {course.university?.name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full ring-1 ring-inset ring-blue-200">
                          {levels.find((l) => l.value === course.level)?.label || course.level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {course.duration}y ({course.studyMode?.replace("_", " ").toLowerCase()})
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-700">{course.tuitionFee || "--"}</p>
                        <p className="text-xs text-gray-400">App: {course.applicationFee || "--"}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => openEditModal(course)} className="text-sm font-medium text-cyan-600 hover:text-cyan-800 transition-colors">
                            Edit
                          </button>
                          <button onClick={() => handleDeleteCourse(course)} className="text-sm font-medium text-red-500 hover:text-red-700 transition-colors">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState title="No courses found" description="Add a new course or adjust your filters." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Page {currentPage} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <AdminModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Course" size="xl">
        <CourseForm onSubmit={handleCreateCourse} formData={formData} handleInputChange={handleInputChange} handleDocReqToggle={handleDocReqToggle} universities={universities} isSubmitting={isSubmitting} onClose={() => setShowCreateModal(false)} />
      </AdminModal>

      {/* Edit Modal */}
      <AdminModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setEditingCourse(null); resetForm(); }} title="Edit Course" size="xl">
        <CourseForm onSubmit={handleEditCourse} formData={formData} handleInputChange={handleInputChange} handleDocReqToggle={handleDocReqToggle} universities={universities} isSubmitting={isSubmitting} onClose={() => { setShowEditModal(false); setEditingCourse(null); resetForm(); }} />
      </AdminModal>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Course"
        message={`Are you sure you want to delete "${deletingCourse?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteCourse}
        onCancel={() => { setShowDeleteModal(false); setDeletingCourse(null); }}
        variant="danger"
      />
    </AdminLayout>
  );
};

export default CourseManagement;
